/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'

import { FormattedMessage } from '@modules/i18n'

import type { SolutionType } from './types'
import type { ReactNode } from 'react'

type SolutionMessage = {
  label: SolutionType
  logo: string
  title: ReactNode
  description: ReactNode
  features: ReactNode[]
}

export function useSolutionOptionsV6(): SolutionMessage[] {
  return [
    {
      label: 'observability',
      logo: 'logoObservability',

      title: (
        <FormattedMessage
          defaultMessage='Observe your applications & infrastructure'
          id='select-solution.title-elastic-for-observability-v6'
        />
      ),

      description: (
        <FormattedMessage
          defaultMessage='For logs, metrics, traces, synthetics'
          id='select-solution.description-observability-v6'
        />
      ),
      features: [
        <FormattedMessage
          defaultMessage='Monitor system and application performance'
          id='select-solution.description-observability-feature0-v6'
        />,
        <FormattedMessage
          defaultMessage='Detect and alert on performance anomalies'
          id='select-solution.description-observability-feature1-v6'
        />,
      ],
    },
    {
      label: 'security',
      logo: 'logoSecurity',
      title: (
        <FormattedMessage
          defaultMessage='Secure your environment'
          id='select-solution.title-elastic-for-security-v6'
        />
      ),

      description: (
        <FormattedMessage
          defaultMessage='For endpoints, networks, and cloud security data'
          id='select-solution.description-security-v6'
        />
      ),

      features: [
        <FormattedMessage
          defaultMessage='Detect and respond to threats with AI-driven security analytics'
          id='select-solution.description-security-feature0-v6'
        />,
        <FormattedMessage
          defaultMessage='Next-gen SIEM, XDR and endpoint, and cloud security '
          id='select-solution.description-security-feature1-b-v6'
        />,
      ],
    },
    {
      label: 'elasticsearch',
      logo: 'logoElasticsearch',
      title: (
        <FormattedMessage
          defaultMessage='Build on the Elasticsearch platform'
          id='select-solution.title-es-v6'
        />
      ),
      description: (
        <FormattedMessage
          defaultMessage='For all data types'
          id='select-solution.description-es-v6'
        />
      ),
      features: [
        <FormattedMessage
          defaultMessage='Ingest, store, and query any data to add search, analytics, and AI to your app '
          id='select-solution.description-es-feature0-v6'
        />,
        <FormattedMessage
          defaultMessage='Leverage a scalable vector database'
          id='select-solution.description-es-feature1-v6'
        />,
      ],
    },
  ]
}
